import { ChangeEvent, useEffect, useMemo } from "react";

import { Autocomplete, SelectEvent } from "@king-ict/eupisi-ui/components";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { Form7InstitutionsRequest } from "../../../../api/Request/RequestApi";
import { FormCard } from "../../../../components";
import { ValidationFailure } from "../../../../models";
import { Form7, Request } from "../../../../models/Request";
import { useGetForm7InstitutionsQuery } from "../../../../queries/requestQuery";

interface Form7InstitutionSelectProps {
  form7: Form7;
  item: Request;
  isEditing: boolean;
  errors?: ValidationFailure[];
  onInstitutionChange: (value: number) => void;
}

export const Form7InstitutionSelect = ({
  form7,
  item,
  isEditing,
  errors,
  onInstitutionChange,
}: Form7InstitutionSelectProps) => {
  const { t } = useTranslation();

  const isDisabled = useMemo(() => {
    return (
      !isEditing ||
      form7.isEarlyEnrollmentRejected ||
      form7.isEnrollmentPostponement ||
      form7.isTemporaryExemptionApproved ||
      form7.isTemporaryExemptionRejected
    );
  }, [
    isEditing,
    form7.isEarlyEnrollmentRejected,
    form7.isEnrollmentPostponement,
    form7.isTemporaryExemptionApproved,
    form7.isTemporaryExemptionRejected,
  ]);

  const selectionCriteria: Form7InstitutionsRequest | undefined =
    useMemo(() => {
      if (!isEditing || isDisabled) return;

      return {
        requestId: item.id,
        isEarlyEnrollmentApproved: form7.isEarlyEnrollmentApproved,
        isEarlyEnrollmentRejected: form7.isEarlyEnrollmentRejected,
        isEnrollmentPostponement: form7.isEnrollmentPostponement,
        isTemporaryExemptionApproved: form7.isTemporaryExemptionApproved,
        isTemporaryExemptionRejected: form7.isTemporaryExemptionRejected,
        isAppropriateProgram: form7.isAppropriateProgram,
        isPreparatoryCourse: form7.isPreparatoryCourse,
      };
    }, [
      isEditing,
      isDisabled,
      item.id,
      form7.isEarlyEnrollmentApproved,
      form7.isEarlyEnrollmentRejected,
      form7.isEnrollmentPostponement,
      form7.isTemporaryExemptionApproved,
      form7.isTemporaryExemptionRejected,
      form7.isAppropriateProgram,
      form7.isPreparatoryCourse,
    ]);

  const { data: institutions, error } = useGetForm7InstitutionsQuery(
    selectionCriteria,
    {
      skip: !selectionCriteria || isDisabled || !item.id,
    }
  );

  useEffect(() => {
    if (error) {
      console.error("Failed to fetch institutions:", error);
    }
  }, [error]);

  const selectData = useMemo(() => {
    const data =
      institutions?.map((institution) => ({
        id: institution.id,
        name: institution.name,
        value: institution.id,
        text: institution.name,
      })) || [];

    const hasSelected = data.some((d) => d.value === form7.institutionId);
    if (!hasSelected && form7.institutionId && form7.institutionName) {
      data.push({
        id: form7.institutionId,
        name: form7.institutionName,
        value: form7.institutionId,
        text: form7.institutionName,
      });
    }

    return {
      data,
      valueProp: "value",
      textProp: "text",
      keyProp: "id",
    };
  }, [institutions, form7.institutionId, form7.institutionName]);

  return (
    <FormCard cardTitle={t("form7.institution")}>
      <Row>
        <Col md={12}>
          <Autocomplete
            label={t("common.expertCommission.institution")}
            name={nameof<typeof form7>("institutionId")}
            value={form7.institutionId}
            onChange={(e: ChangeEvent<SelectEvent>) =>
              onInstitutionChange(Number(e.target.value))
            }
            selectData={selectData}
            disabled={isDisabled}
            errorText={
              errors?.find((e) => e.propertyName?.startsWith("InstitutionId"))
                ?.message
            }
          />
        </Col>
      </Row>
    </FormCard>
  );
};
